import React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { reduxForm } from 'redux-form';
import { PagedTableContainer } from '../common/components/PagedTable/PagedTableContainer';
import { TEST_TYPES_SERVICE } from '../common/services/availableServices';
import ReleaseStatusSelectField from "../common/fields/selectFields/ReleaseStatusSelectField";
import { translatable } from 'react-multilingual';
import { compose } from 'recompose';
import { columnType } from '../common/components/PagedTable/columnType';
import SearchTextField from '../common/fields/SearchTextField';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import { managePageStyles } from '../styles';
import StatusComponent from './StatusComponent';
import Utils from '../common/Utils';
import Dialog from '@material-ui/core/Dialog/Dialog';
import TestDetails from './TestDetails';
import { roles } from '../Auth/roles';
import PermissionContainer from '../Auth/PermissionContainer';

const styles = () => ({
  dialogPaperClass: {
    padding: 0,
    width: '120vh'
  }
});

class TestManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      criteria: null,
      openModal: false,
      testId: null,
      test: null,
      loadingTableData: true,
    };
    this.tableColumnSetup();
  }

  createButtonClickHandler = () => {
    this.setState({
      openModal: true,
      testId: NaN,
      test: {
        name: '',
        manufacturer: '',
        calibrationFormula: '',
        sampleType: '',
        unit: '',
        status: 'VALIDATION',
        markerMM: 8,
        distMarkers: 29,
      },
    });
  };

  tableColumnSetup() {
    this.columns = [
      { key: 'id', type: columnType.text },
      { key: 'name', type: columnType.text },
      { key: 'createdDate', type: columnType.text, customCellRenderer: this.dateCellRenderer },
      { key: 'status', type: columnType.text, customCellRenderer: this.statusCellRenderer },
      { key: 'sampleType', type: columnType.text },
      { key: 'manufacturer', type: columnType.text },
      // { key: 'details', type: columnType.text, customCellRenderer: this.detailsCellRenderer, sortable: false }
    ];
  }

  dateCellRenderer = (rowId, columnValue) => {
    return Utils.shortDateRenderer(new Date(columnValue), '-');
  };

  statusCellRenderer = (rowId, columnValue) => {
    return <StatusComponent releaseStatus={columnValue} />;
  };

  // detailsCellRenderer = (rowId) => {
  //   return <Button onClick={this.detailsButtonClickHandler(rowId)}>{this.props.labels.buttons.details}</Button>;
  // };

  detailsButtonClickHandler = row => {
    this.setState({
      openModal: true,
      testId: row.id,
      test: {
        name: row.name,
        manufacturer: row.manufacturer,
        calibrationFormula: row.calibrationFormulaType,
        sampleType: row.sampleType,
        unit: row.unit,
        status: row.status,
        markerMM: row.markerMM,
        distMarkers: row.distMarkers,
      }
    });
  };

  closeWindowHandler = () => {
    this.setState({ testId: null, openModal: false, test: null });
  };

  searchButtonClickHandler = values => {
    let queryParams = {
      search: values.search
    };
    if ('status' in values) {
      queryParams = {
        ...queryParams,
        'status': values.status
      };
    }
    this.setState({
      criteria: queryParams,
      loadingTableData: true,
    });
  };

  successHandler = () => {
    this.setState({ testId: null, test: null, openModal: false, loadingTableData: true });
  };

  loadingDataFinishHandler = () => {
    this.setState({
      loadingTableData: false,
    });
  };

  render() {
    const { handleSubmit, labels, classes } = this.props;
    const specLabels = labels.test_management;
    const { criteria, openModal, loadingTableData } = this.state;

    return (
      <div>
        <Dialog classes={{ paper: classes.dialogPaperClass }}
          maxWidth={false}
          open={openModal}
          onClose={this.closeWindowHandler}
        >
          <TestDetails
            testId={this.state.testId}
            test={this.state.test}
            onCancel={this.closeWindowHandler}
            onSuccess={this.successHandler}
          />
        </Dialog>

        <Grid container className={classes.header}>
          <Grid item xs={4}>
            <Typography variant="h5" color="primary">
              {specLabels.page_title}
            </Typography>
          </Grid>
          <PermissionContainer allowedRoles={[roles.ADMINISTRATOR]}>
            <Grid item xs={8}>
              <Grid container spacing={32} alignItems="center" justify="flex-end">
                <Grid item>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    onClick={this.createButtonClickHandler}
                  >
                    {specLabels.buttons.create_test}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </PermissionContainer>
        </Grid>

        <form onSubmit={handleSubmit(this.searchButtonClickHandler)}>
          <Grid spacing={24} container className={classes.criteria}>
            <Grid item md={4}>
              <SearchTextField
                name="search"
                className={classes.criteriaField}
              />
            </Grid>
            <Grid item md={3}>
              <div style={{ marginTop: 16, marginBottom: 8 }}> {/* align select field to the search bar */}
                <ReleaseStatusSelectField
                  optionmap={[
                    { value: 'IN_PRODUCTION', label: 'IN_PRODUCTION' },
                    { value: 'WITHDRAWN', label: 'WITHDRAWN' },
                    { value: 'VALIDATION', label: 'VALIDATION' }
                  ]}
                  name="status"
                  className={classes.criteriaField}
                />
              </div>
            </Grid>
            <Grid item container md={5} alignItems="center" justify="flex-end">
              <Button
                variant="contained"
                color="primary"
                type="submit">
                {labels.buttons.search}
              </Button>
            </Grid>
          </Grid>
        </form>
        <PagedTableContainer
          rowKey="id"
          orderBy="id"
          order="asc"
          columns={this.columns}
          criteria={criteria}
          service={TEST_TYPES_SERVICE}
          loadingData={loadingTableData}
          onLoadingDataFinish={this.loadingDataFinishHandler}
          errorMessage={labels.table.error_message}
          emptyResultMessage={labels.table.empty_result}
          columnLabels={specLabels.columns}
          onRowClick={this.detailsButtonClickHandler}
        />
      </div>
    );
  }
}

const mapTranslationsToProps = ({ labels }) => ({ labels });

export default compose(
  translatable(mapTranslationsToProps),
  reduxForm({ form: 'TestSearch' }),
  withStyles(
    (theme) => ({
      ...managePageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true },
  )
)(TestManagement); 